import { getCredentials } from './credentials';
import { signPut } from './sigv4';
import type { TelemetryConfig, EnrichedEvent } from './types';

type SerializableEvent = Omit<EnrichedEvent, 'metadata'> & { metadata: string | null };

export const toSerializable = (e: EnrichedEvent): SerializableEvent => ({
  ...e,
  metadata: e.metadata ? JSON.stringify(e.metadata) : null,
});

// Blob().stream() keeps the type-system happy with ReadableStream<Uint8Array<ArrayBuffer>>.
// A manual ReadableStream + TextEncoder would save one allocation per flush but
// would require Uint8Array<ArrayBufferLike> → Uint8Array<ArrayBuffer> casts that
// fight the compiler more than they save runtime. Blob construction is µs-cheap.
const gzip = async (input: string): Promise<Uint8Array<ArrayBuffer>> => {
  const stream = new Blob([input]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

// Feature-detect for Safari < 16.4 and old Chromium/Firefox. We fail-closed
// (throw at init via tracker) instead of producing a confusing ReferenceError
// at the first flush. Documented browser baseline in README.
export const hasCompressionStream = (): boolean =>
  typeof CompressionStream !== 'undefined';

export const uploadBatch = async (
  events: EnrichedEvent[],
  key: string,
  config: TelemetryConfig,
): Promise<void> => {
  const creds = await getCredentials(config.authProvider);
  const jsonl = events.map((e) => JSON.stringify(toSerializable(e))).join('\n');
  const body = await gzip(jsonl);

  const signed = await signPut({
    accessKeyId: creds.accessKeyId,
    secretAccessKey: creds.secretAccessKey,
    sessionToken: creds.sessionToken,
    region: config.region,
    bucket: config.bucketName,
    key,
    body,
    contentType: 'application/x-ndjson',
  });

  const response = await fetch(signed.url, {
    method: 'PUT',
    headers: signed.headers,
    body: body as BodyInit,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `S3 PUT ${response.status} ${response.statusText}: ${text || '(no body)'}`,
    );
  }
};
